import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import StatsCard from "../AdminDashboard/StatsCard";
import { useToast } from "../../hook/useToast";
import {
  createChannelPartnerCommunityPartner,
  deleteChannelPartnerCommunityPartner,
  fetchChannelPartnerCommunityPartners,
  fetchChannelPartnerCommunityPartnersStats,
  fetchChannelPartnerCommunityPartnerStats,
  fetchChannelPartnerCommunityPartnerStudents,
  updateChannelPartnerCommunityPartner,
} from "../../services/channelPartnerPortalApi";
import ChannelPartnerDashboardLayout from "./ChannelPartnerDashboardLayout";
import { COMMUNITY_REFERRAL_EARNING_RUPEES } from "./earningRates";

const EMPTY_FORM = { name: "", city: "", email: "", phone_number: "" };
const STUDENT_PAGE_SIZE = 20;

const headerCellSx = {
  bgcolor: "#F7F7FA",
  color: "#696974",
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
  borderBottom: "1px solid #EFEFF4",
};

const bodyCellSx = {
  color: "#44444F",
  fontSize: 13,
  borderBottom: "1px solid #F1F1F5",
};

const getErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  if (responseData?.message) return responseData.message;
  if (responseData?.errors) {
    const firstValue = Object.values(responseData.errors)[0];
    if (Array.isArray(firstValue)) return String(firstValue[0]);
    if (typeof firstValue === "string") return firstValue;
  }
  return fallback;
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatCurrency = (value) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(Number(value || 0));

const buildReferralUrl = (partner) => {
  if (!partner) return "";
  const path = partner.referral_path
    || (partner.short_code
      ? `/r/${partner.short_code}`
      : `/login?role=Student&community_partner=${partner.referral_code}`);
  const configuredStudentAppUrl = import.meta.env.VITE_STUDENT_APP_URL?.trim();

  try {
    const studentAppOrigin = new URL(
      configuredStudentAppUrl || window.location.origin,
      window.location.origin,
    ).origin;
    return new URL(path, studentAppOrigin).toString();
  } catch {
    return new URL(path, window.location.origin).toString();
  }
};

const ChannelPartnerCommunityPartnersPage = ({ workspaceOnly = false }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPartnerId = searchParams.get("partner");
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [partnersError, setPartnersError] = useState("");
  const [overviewStats, setOverviewStats] = useState({});
  const [overviewStatsLoading, setOverviewStatsLoading] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState("");
  const [studentPage, setStudentPage] = useState(1);
  const [studentPagination, setStudentPagination] = useState({
    page: 1,
    page_size: STUDENT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  });
  const [studentSearch, setStudentSearch] = useState("");
  const [debouncedStudentSearch, setDebouncedStudentSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletePartner, setDeletePartner] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const selectedPartner = useMemo(
    () => partners.find(
      (partner) => partner.community_partner_id === Number(selectedPartnerId),
    ) || null,
    [partners, selectedPartnerId],
  );

  const loadPartners = useCallback(async (preferredPartnerId) => {
    setPartnersLoading(true);
    setPartnersError("");
    try {
      const response = await fetchChannelPartnerCommunityPartners({ pageSize: 100 });
      const nextPartners = response?.data?.data?.community_partners || [];
      setPartners(nextPartners);
      setSelectedPartnerId((currentId) => {
        const preferred = Number(preferredPartnerId);
        if (preferred && nextPartners.some(
          (partner) => partner.community_partner_id === preferred,
        )) return preferred;
        if (currentId && nextPartners.some(
          (partner) => partner.community_partner_id === Number(currentId),
        )) return Number(currentId);
        return nextPartners[0]?.community_partner_id || "";
      });
    } catch (error) {
      setPartnersError(getErrorMessage(error, "Unable to load community partners."));
    } finally {
      setPartnersLoading(false);
    }
  }, []);

  const loadOverviewStats = useCallback(async () => {
    setOverviewStatsLoading(true);
    try {
      const response = await fetchChannelPartnerCommunityPartnersStats();
      setOverviewStats(response?.data?.data || {});
    } catch (error) {
      showToast(
        getErrorMessage(error, "Unable to load community partner statistics."),
        "error",
      );
    } finally {
      setOverviewStatsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadPartners(workspaceOnly ? requestedPartnerId : undefined);
  }, [loadPartners, requestedPartnerId, workspaceOnly]);

  useEffect(() => {
    if (!workspaceOnly) loadOverviewStats();
  }, [loadOverviewStats, workspaceOnly]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedStudentSearch(studentSearch.trim()),
      350,
    );
    return () => window.clearTimeout(timer);
  }, [studentSearch]);

  useEffect(() => {
    setStudentPage(1);
  }, [selectedPartnerId, debouncedStudentSearch]);

  useEffect(() => {
    if (!selectedPartnerId) {
      setStats({});
      setStudents([]);
      setStudentPagination({
        page: 1,
        page_size: STUDENT_PAGE_SIZE,
        total_records: 0,
        total_pages: 0,
      });
      return undefined;
    }

    let active = true;
    setStatsLoading(true);
    fetchChannelPartnerCommunityPartnerStats(selectedPartnerId)
      .then((response) => {
        if (active) setStats(response?.data?.data || {});
      })
      .catch((error) => {
        if (active) showToast(
          getErrorMessage(error, "Unable to load partner statistics."),
          "error",
        );
      })
      .finally(() => {
        if (active) setStatsLoading(false);
      });
    return () => { active = false; };
  }, [selectedPartnerId, showToast]);

  useEffect(() => {
    if (!selectedPartnerId) return undefined;
    let active = true;
    setStudentsLoading(true);
    setStudentsError("");
    fetchChannelPartnerCommunityPartnerStudents(selectedPartnerId, {
      page: studentPage,
      pageSize: STUDENT_PAGE_SIZE,
      search: debouncedStudentSearch,
    })
      .then((response) => {
        if (!active) return;
        const payload = response?.data?.data || {};
        setStudents(payload.students || []);
        setStudentPagination(payload.pagination || {});
      })
      .catch((error) => {
        if (active) setStudentsError(
          getErrorMessage(error, "Unable to load this partner's students."),
        );
      })
      .finally(() => {
        if (active) setStudentsLoading(false);
      });
    return () => { active = false; };
  }, [selectedPartnerId, studentPage, debouncedStudentSearch]);

  const openCreateDialog = () => {
    setEditingPartner(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setFormOpen(true);
  };

  const openEditDialog = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || "",
      city: partner.city || "",
      email: partner.email || "",
      phone_number: partner.phone_number || "",
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (!value.trim()) errors[key] = "This field is required.";
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Enter a valid email address.";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const savePartner = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const response = editingPartner
        ? await updateChannelPartnerCommunityPartner(
          editingPartner.community_partner_id,
          formData,
        )
        : await createChannelPartnerCommunityPartner(formData);
      const savedPartner = response?.data?.data;
      showToast(
        editingPartner
          ? "Community partner updated successfully."
          : "Community partner added successfully.",
        "success",
      );
      setFormOpen(false);
      await loadPartners(savedPartner?.community_partner_id);
      await loadOverviewStats();
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        setFormErrors(Object.fromEntries(
          Object.entries(apiErrors).map(([key, value]) => [
            key,
            Array.isArray(value) ? String(value[0]) : String(value),
          ]),
        ));
      }
      showToast(getErrorMessage(error, "Unable to save community partner."), "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletePartner) return;
    setDeleting(true);
    try {
      await deleteChannelPartnerCommunityPartner(
        deletePartner.community_partner_id,
      );
      showToast("Community partner deleted successfully.", "success");
      setDeletePartner(null);
      await loadPartners();
      await loadOverviewStats();
    } catch (error) {
      showToast(getErrorMessage(error, "Unable to delete community partner."), "error");
    } finally {
      setDeleting(false);
    }
  };

  const copyReferralUrl = async (partner) => {
    try {
      await navigator.clipboard.writeText(buildReferralUrl(partner));
      showToast("Unique student URL copied.", "success");
    } catch {
      showToast("Could not copy the URL. Please copy it manually.", "error");
    }
  };

  return (
    <ChannelPartnerDashboardLayout>
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "stretch", lg: "center" }, flexDirection: { xs: "column", lg: "row" }, gap: 2, mb: 1.5 }}>
          <Box sx={{ flexShrink: 0 }}>
            {workspaceOnly && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/channel-partner-portal/community-partners")}
                sx={{ p: 0, mb: 1, color: "#696974", textTransform: "none" }}
              >
                Back to Community Partners
              </Button>
            )}
            <Typography sx={{ fontSize: 21, fontWeight: 700, color: "#171725" }}>
              {workspaceOnly ? "Community Partner Work" : "Community Partners"}
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: 13, color: "#92929D" }}>
              {workspaceOnly
                ? "Select one partner to review their student statistics and completed work."
                : "Create and manage partners and their unique student registration URLs."}
            </Typography>
          </Box>
          {!workspaceOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={{ bgcolor: "#2D315F", textTransform: "none", borderRadius: "8px", px: 2, "&:hover": { bgcolor: "#24284E" } }}
            >
              Add Community Partner
            </Button>
          )}
          {workspaceOnly && (
            <Box
              sx={{
                ml: { lg: "auto" },
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", lg: "flex-end" },
                gap: 1.5,
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#696974", whiteSpace: "nowrap" }}>
                Select one Community Partner
              </Typography>
              <Select
                size="small"
                displayEmpty
                value={selectedPartnerId}
                onChange={(event) => setSelectedPartnerId(event.target.value)}
                disabled={partnersLoading || partners.length === 0}
                sx={{ minWidth: 210, borderRadius: "8px", fontSize: 13, bgcolor: "#fff" }}
              >
                <MenuItem value="" disabled>Select a partner</MenuItem>
                {partners.map((partner) => (
                  <MenuItem key={partner.community_partner_id} value={partner.community_partner_id}>{partner.name}</MenuItem>
                ))}
              </Select>
              {selectedPartner && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#2D315F" }}>{selectedPartner.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#92929D" }}>{selectedPartner.email}</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {partnersError && <Alert severity="error" sx={{ mb: 2 }}>{partnersError}</Alert>}

        {!workspaceOnly && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2, mb: 2.2 }}>
            <StatsCard value={overviewStatsLoading ? "--" : (overviewStats.total_partners ?? 0)} label="Total Partners" icon="total" bgColor="#F5F6FA" />
            <StatsCard value={overviewStatsLoading ? "--" : (overviewStats.total_students ?? 0)} label="Total Students" icon="total" bgColor="#F2F6FF" />
            <StatsCard value={overviewStatsLoading ? "--" : (overviewStats.students_this_month ?? 0)} label="Students This Month" icon="month" bgColor="#FFF9F1" />
            <StatsCard value={overviewStatsLoading ? "--" : (overviewStats.students_this_year ?? 0)} label="Students This Year" icon="year" bgColor="#F2FBF7" />
          </Box>
        )}

        {!workspaceOnly && <Paper elevation={0} sx={{ border: "1px solid #EFEFF4", borderRadius: "8px", bgcolor: "#fff", mb: 2.2, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #EFEFF4", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#171725" }}>
              Community Partner List
            </Typography>
            {partnersLoading && <CircularProgress size={22} />}
          </Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 1050 }}>
              <TableHead>
                <TableRow>
                  {["Name", "City", "Email ID", "Phone Number", "Unique URL", "Students", "Partner"].map((label) => (
                    <TableCell key={label} sx={headerCellSx}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!partnersLoading && partners.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: "#92929D" }}>No community partners added yet.</TableCell></TableRow>
                )}
                {partners.map((partner) => {
                  return (
                    <TableRow key={partner.community_partner_id} hover>
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: "#2D315F" }}>{partner.name}</TableCell>
                      <TableCell sx={bodyCellSx}>{partner.city}</TableCell>
                      <TableCell sx={bodyCellSx}>{partner.email}</TableCell>
                      <TableCell sx={bodyCellSx}>{partner.phone_number}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, maxWidth: 300 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography component="a" href={buildReferralUrl(partner)} target="_blank" rel="noreferrer" sx={{ color: "#3568D4", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 235 }}>
                            {buildReferralUrl(partner)}
                          </Typography>
                          <Tooltip title="Copy unique URL"><IconButton size="small" onClick={() => copyReferralUrl(partner)}><ContentCopyOutlinedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>{partner.student_count ?? 0}</TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Open partner work page"><IconButton size="small" onClick={() => navigate(`/channel-partner-portal/community-partners/work?partner=${partner.community_partner_id}`)}><VisibilityOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditDialog(partner)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeletePartner(partner)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>}

        {workspaceOnly && (selectedPartner ? (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }, gap: 2, mb: 2.2 }}>
              <StatsCard value={statsLoading ? "--" : (stats.total_students ?? 0)} label="Total Students" icon="total" bgColor="#F5F6FA" />
              <StatsCard value={statsLoading ? "--" : (stats.students_this_month ?? 0)} label="Students This Month" icon="month" bgColor="#FFF9F1" />
              <StatsCard value={statsLoading ? "--" : (stats.students_this_year ?? 0)} label="Students This Year" icon="year" bgColor="#F2F6FF" />
              <StatsCard value={statsLoading ? "--" : formatCurrency(stats.total_earnings)} label="Referral Earnings" icon="total" bgColor="#ECFDF3" />
            </Box>

            <Paper elevation={0} sx={{ border: "1px solid #EFEFF4", borderRadius: "8px", bgcolor: "#fff", overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1.4, borderBottom: "1px solid #EFEFF4", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#171725" }}>{selectedPartner.name} Students</Typography>
                <TextField
                  size="small"
                  value={studentSearch}
                  onChange={(event) => setStudentSearch(event.target.value)}
                  placeholder="Search students"
                  sx={{ width: 260, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13 } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 19, color: "#92929D" }} /></InputAdornment> }}
                />
              </Box>
              {studentsError && <Alert severity="error" sx={{ m: 2 }}>{studentsError}</Alert>}
              <TableContainer>
                <Table size="small" sx={{ minWidth: 1200 }}>
                  <TableHead><TableRow>
                    {["Student Name", "School", "Board", "Class", "City", "Registered", "Tests Taken", "Counselling Taken", "Skill Taken", "Referral Status", "Earning"].map((label) => <TableCell key={label} sx={headerCellSx}>{label}</TableCell>)}
                  </TableRow></TableHead>
                  <TableBody>
                    {studentsLoading && <TableRow><TableCell colSpan={11} align="center" sx={{ py: 5 }}><CircularProgress size={28} /></TableCell></TableRow>}
                    {!studentsLoading && students.length === 0 && <TableRow><TableCell colSpan={11} align="center" sx={{ py: 5, color: "#92929D" }}>No students are registered through this partner yet.</TableCell></TableRow>}
                    {!studentsLoading && students.map((student) => (
                      <TableRow key={student.student_id} hover>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>{student.name || "--"}</TableCell>
                        <TableCell sx={bodyCellSx}>{student.school || "--"}</TableCell>
                        <TableCell sx={bodyCellSx}>{student.board || "--"}</TableCell>
                        <TableCell sx={bodyCellSx}>{student.class_name ?? "--"}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, maxWidth: 220 }}>{student.city || "--"}</TableCell>
                        <TableCell sx={bodyCellSx}>{formatDate(student.registered_at)}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{student.tests_taken ?? 0}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{student.counselling_taken ?? 0}</TableCell>
                        <TableCell align="center" sx={bodyCellSx}>{student.skill_taken ?? 0}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Chip
                            size="small"
                            label={student.earning_status === "earned" ? "Earned" : "Pending test"}
                            sx={{
                              borderRadius: "6px",
                              bgcolor: student.earning_status === "earned" ? "#ECFDF3" : "#FFF4E8",
                              color: student.earning_status === "earned" ? "#027A48" : "#B54708",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            ...bodyCellSx,
                            color: student.earning_status === "earned" ? "#027A48" : "#B54708",
                            fontWeight: 750,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {student.earning_status === "earned"
                            ? `+${formatCurrency(student.earning_amount)}`
                            : `${formatCurrency(
                              stats.referral_earning_rate
                                || COMMUNITY_REFERRAL_EARNING_RUPEES,
                            )} Pending`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ minHeight: 56, px: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #EFEFF4" }}>
                <Box>
                  <Typography sx={{ fontSize: 12, color: "#92929D" }}>{studentPagination.total_records || 0} students</Typography>
                  <Typography sx={{ mt: 0.3, fontSize: 12, color: "#027A48", fontWeight: 700 }}>
                    Total referral earnings: {formatCurrency(stats.total_earnings)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton size="small" disabled={studentPage <= 1 || studentsLoading} onClick={() => setStudentPage((page) => page - 1)}><KeyboardArrowLeftIcon /></IconButton>
                  <Typography sx={{ fontSize: 12, color: "#696974" }}>Page {studentPagination.page || 1} of {studentPagination.total_pages || 1}</Typography>
                  <IconButton size="small" disabled={studentsLoading || !studentPagination.total_pages || studentPage >= studentPagination.total_pages} onClick={() => setStudentPage((page) => page + 1)}><KeyboardArrowRightIcon /></IconButton>
                </Box>
              </Box>
            </Paper>
          </>
        ) : (
          !partnersLoading && <Alert severity="info">No Community Partner is available. Return to the Community Partners page to add one.</Alert>
        ))}
      </Box>

      {!workspaceOnly && <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editingPartner ? "Edit Community Partner" : "Add Community Partner"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, pt: 0.5 }}>
            {[
              ["name", "Name"],
              ["city", "City"],
              ["email", "Email ID"],
              ["phone_number", "Phone Number"],
            ].map(([key, label]) => (
              <TextField
                key={key}
                label={label}
                value={formData[key]}
                onChange={(event) => {
                  setFormData((current) => ({ ...current, [key]: event.target.value }));
                  setFormErrors((current) => ({ ...current, [key]: "" }));
                }}
                error={Boolean(formErrors[key])}
                helperText={formErrors[key] || " "}
                fullWidth
                size="small"
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={savePartner} disabled={saving} sx={{ bgcolor: "#2D315F", textTransform: "none", "&:hover": { bgcolor: "#24284E" } }}>{saving ? "Saving..." : (editingPartner ? "Save Changes" : "Add Partner")}</Button>
        </DialogActions>
      </Dialog>}

      {!workspaceOnly && <Dialog open={Boolean(deletePartner)} onClose={() => !deleting && setDeletePartner(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Community Partner?</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: 14, color: "#696974" }}>This removes {deletePartner?.name} from the active partner list. Existing student attribution is preserved.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeletePartner(null)} disabled={deleting} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting} sx={{ textTransform: "none" }}>{deleting ? "Deleting..." : "Delete"}</Button>
        </DialogActions>
      </Dialog>}
    </ChannelPartnerDashboardLayout>
  );
};

ChannelPartnerCommunityPartnersPage.propTypes = {
  workspaceOnly: PropTypes.bool,
};

export default ChannelPartnerCommunityPartnersPage;
